import { Component } from '@theme/component';

/**
 * Progressive enhancement for the sample order form. The checkboxes submit
 * natively with the Shopify contact form (associated via the `form`
 * attribute), so the form works without JavaScript. This adds a running
 * count/total and blocks submission when no sample is chosen.
 *
 * @extends {Component<{ summary: HTMLElement, error: HTMLElement }>}
 */
class CrSampleOrder extends Component {
  requiredRefs = ['summary', 'error'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#update);
    this.form?.addEventListener('submit', this.#onSubmit);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#update);
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  get form() {
    return this.querySelector('form');
  }

  get #checked() {
    return /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[type="checkbox"]:checked')));
  }

  #update = () => {
    const checked = this.#checked;
    const total = checked.reduce((sum, input) => sum + (parseFloat(input.dataset.price ?? '0') || 0), 0);
    // Sample prices are fixed in the store currency (see the section), not converted per market.
    const currency = this.dataset.currency || 'EUR';
    const price = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: Number.isInteger(total) ? 0 : 2,
    }).format(total);
    const count = checked.length;
    const priced = checked.some((input) => (input.dataset.price ?? '').trim() !== '');
    const { labelOne = '[count] sample · [price]', labelOther = '[count] samples · [price]' } = this.refs.summary.dataset;
    let label = count === 1 ? labelOne : labelOther;
    // Samples without a price show the count alone.
    if (!priced) label = label.replace(/\s*·?\s*\[price\]/, '');
    this.refs.summary.textContent = label.replace('[count]', String(count)).replace('[price]', price);
    if (count > 0) this.refs.error.hidden = true;
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (this.#checked.length > 0) return;
    event.preventDefault();
    this.refs.error.hidden = false;
    this.querySelector('input[type="checkbox"]')?.focus();
  };
}

if (!customElements.get('cr-sample-order')) customElements.define('cr-sample-order', CrSampleOrder);
